import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface DocumentGrouping {
  id: string;
  name: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
}

export interface GetAllDocumentGroupingsParams {
  cursor?: string;
  limit?: number;
}

export interface GetAllDocumentGroupingsResponse {
  success: boolean;
  groupings: DocumentGrouping[];
  nextCursor?: string;
}

const getAllDocumentGroupings: QueryFunction<
  GetAllDocumentGroupingsResponse,
  ['/document-groupings', GetAllDocumentGroupingsParams]
> = async ({ queryKey }) => {
  if (isMockServices()) return await mock(_mockData);

  const { cursor, limit } = queryKey[1];
  const authTokenHttp = await getCompatibleAuthTokenHttp();

  const queryParams = new URLSearchParams();
  if (cursor) queryParams.append('cursor', cursor);
  if (limit) queryParams.append('limit', limit.toString());

  const response = await authTokenHttp<GetAllDocumentGroupingsResponse>(
    `/document-groupings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    {
      method: 'GET',
      paginate: true,
      accumulateKey: 'groupings',
      nextCursorKey: 'nextCursor',
      limit: limit || 100,
    },
  );

  if (response.data) {
    // Exclude configuration from the response as per requirements
    const groupingsWithoutConfig = response.data.groupings.map((group) => {
      return {
        id: group.id,
        name: group.name,
        userId: group.userId,
        createdAt: group.createdAt,
        updatedAt: group.updatedAt,
        isDeleted: group.isDeleted,
      };
    });
    return {
      ...response.data,
      groupings: groupingsWithoutConfig,
    };
  }
  throw new Error('Failed to fetch document groupings');
};

export default getAllDocumentGroupings;

const _mockData: GetAllDocumentGroupingsResponse = {
  success: true,
  groupings: [
    {
      id: 'mock-1',
      name: 'Sample Document Stack',
      userId: 'user-123',
      createdAt: '2025-01-01T00:00:00Z',
      updatedAt: '2025-01-01T00:00:00Z',
      isDeleted: false,
    },
  ],
  nextCursor: undefined,
};
